import { liveFrameToJointPositions } from '../skeletonConstants';

/**
 * LiveStore — per-session live state, kept outside React so a 10 Hz skeleton stream
 * for one session only re-renders the components subscribed to that session.
 *
 * Keys: 'list' (session:list), 'errors' (relay:error), or a sessionId.
 */
const EVENT_CAP = 200;
const HISTORY_CAP = 120; // 120 × 0.25 s = 30 s of signal history
const HISTORY_MS = 250;
const TRAIL_CAP = 120; // pelvis trail, ~15 s at 8 Hz
const TRAIL_MS = 125;

function emptySession(sessionId) {
    return {
        sessionId,
        status: null, // 'active' | 'playing' | 'paused' | 'ended' | 'disconnected'
        statusAt: 0,
        game: null,
        skeleton: null,
        skeletonAt: 0,
        suki: null,
        sukiAt: 0,
        events: [],
        signalHistory: {}, // name -> number[] (0/1)
        rangeHistory: {}, // name -> number[]
        trail: [], // [[x, y, z]] pelvis positions
        joinError: null,
        ended: false,
        gone: false, // no longer listed by session:list
    };
}

export function createLiveStore() {
    let list = { sessions: [], loaded: false, error: null, at: 0 };
    let errors = [];
    const sessions = new Map();
    const listeners = new Map();
    const lastHist = new Map();
    const lastTrail = new Map();

    const emit = (key) => {
        const set = listeners.get(key);
        if (set) set.forEach((fn) => fn());
    };
    const subscribe = (key, fn) => {
        if (!listeners.has(key)) listeners.set(key, new Set());
        listeners.get(key).add(fn);
        return () => listeners.get(key)?.delete(fn);
    };
    const get = (sid) => sessions.get(sid) || null;
    const patch = (sid, fn) => {
        const cur = sessions.get(sid) || emptySession(sid);
        const next = fn(cur);
        if (next !== cur) {
            sessions.set(sid, next);
            emit(sid);
        }
    };

    return {
        subscribe,
        getList: () => list,
        getErrors: () => errors,
        get,

        setList(raw, error = null) {
            if (error) {
                list = { ...list, loaded: true, error, at: Date.now() };
                emit('list');
                return;
            }
            const arr = Array.isArray(raw) ? raw.filter((s) => s && s.sessionId) : [];
            const ids = new Set(arr.map((s) => s.sessionId));
            // Sessions that dropped off the list have ended for the portal: clear their events.
            list.sessions.forEach((s) => {
                if (!ids.has(s.sessionId) && sessions.has(s.sessionId)) {
                    patch(s.sessionId, (cur) => ({ ...cur, gone: true, events: [], skeleton: null, suki: null }));
                }
            });
            arr.forEach((s) => {
                if (sessions.has(s.sessionId) && sessions.get(s.sessionId).gone) {
                    patch(s.sessionId, (cur) => ({ ...cur, gone: false, ended: false }));
                }
            });
            const same = arr.length === list.sessions.length && arr.every((s, i) => {
                const o = list.sessions[i];
                return o && o.sessionId === s.sessionId && o.game === s.game && o.userId === s.userId && o.startTime === s.startTime;
            });
            if (same && list.loaded && !list.error) return;
            list = { sessions: arr, loaded: true, error: null, at: Date.now() };
            emit('list');
        },

        onSkeleton(sid, frame) {
            const now = Date.now();
            patch(sid, (cur) => {
                let trail = cur.trail;
                if (now - (lastTrail.get(sid) || 0) >= TRAIL_MS) {
                    const j = liveFrameToJointPositions(frame);
                    const p = j.Pelvis || j.SpineNaval;
                    if (p) {
                        trail = [...trail.slice(-(TRAIL_CAP - 1)), [p.x, p.y, p.z]];
                        lastTrail.set(sid, now);
                    }
                }
                return { ...cur, skeleton: frame, skeletonAt: now, trail, status: cur.status || 'active' };
            });
        },

        onSuki(sid, data) {
            const now = Date.now();
            patch(sid, (cur) => {
                let { signalHistory, rangeHistory } = cur;
                if (now - (lastHist.get(sid) || 0) >= HISTORY_MS) {
                    lastHist.set(sid, now);
                    signalHistory = { ...signalHistory };
                    Object.entries(data.s || {}).forEach(([k, val]) => {
                        signalHistory[k] = [...(signalHistory[k] || []).slice(-(HISTORY_CAP - 1)), val ? 1 : 0];
                    });
                    rangeHistory = { ...rangeHistory };
                    Object.entries(data.r || {}).forEach(([k, val]) => {
                        const n = parseFloat(val);
                        rangeHistory[k] = [...(rangeHistory[k] || []).slice(-(HISTORY_CAP - 1)), Number.isFinite(n) ? n : null];
                    });
                }
                return { ...cur, suki: data, sukiAt: now, signalHistory, rangeHistory, status: cur.status || 'active' };
            });
        },

        onEvent(sid, data) {
            patch(sid, (cur) => {
                const evt = { ...data, _id: Math.random().toString(36).slice(2, 10), _localTs: Date.now() };
                const events = cur.events.length >= EVENT_CAP ? [...cur.events.slice(-(EVENT_CAP - 1)), evt] : [...cur.events, evt];
                return { ...cur, events };
            });
        },

        onStatus(sid, data) {
            const status = data.status;
            patch(sid, (cur) => {
                const ended = status === 'ended';
                return {
                    ...cur,
                    status,
                    statusAt: Date.now(),
                    game: data.game || cur.game,
                    ended,
                    // A finished session keeps its status but drops the live buffers.
                    events: ended ? [] : cur.events,
                    skeleton: ended ? null : cur.skeleton,
                    suki: ended ? null : cur.suki,
                };
            });
        },

        setJoinError(sid, error) {
            patch(sid, (cur) => ({ ...cur, joinError: error }));
        },

        addError(err) {
            errors = [...errors.slice(-19), { ...err, id: Math.random().toString(36).slice(2), at: Date.now() }];
            emit('errors');
        },

        forget(sid) {
            if (sessions.delete(sid)) emit(sid);
        },

        clearAll() {
            sessions.clear();
            list = { sessions: [], loaded: false, error: null, at: 0 };
            errors = [];
            listeners.forEach((set) => set.forEach((fn) => fn()));
        },
    };
}
